import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/utils-ros";
import { toast } from "sonner";
import { Frown, RotateCcw, Radio, Zap, Target, AlertTriangle, Loader2, Building2, Trash2, Filter, X, ChevronDown, ChevronRight, TrendingDown } from "lucide-react";
import { useAuth, canRestoreTrashType } from "@/lib/auth";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { EditableField } from "@/components/revenueos/EditableField";

const TYPE_META = {
  company: { icon: Building2, color: "var(--saarv-blue)", label: "Company" },
  signal: { icon: Radio, color: "var(--info)", label: "Signal" },
  lead: { icon: Zap, color: "var(--warning)", label: "Lead" },
  opportunity: { icon: Target, color: "var(--copper)", label: "Opportunity" },
};

const TYPE_TABS = [
  { key: "all", label: "All types", countKey: null },
  { key: "company", label: "Companies", countKey: "companies" },
  { key: "signal", label: "Signals", countKey: "signals" },
  { key: "lead", label: "Leads", countKey: "leads" },
  { key: "opportunity", label: "Opportunities", countKey: "opportunities" },
];

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)}>
        {options.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
    </label>
  );
}

export default function Trash() {
  const { user, role } = useAuth();
  const canPurge = role === "super_admin" || role === "admin" || role === "founder";
  const [data, setData] = useState({ companies: [], signals: [], leads: [], opportunities: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setData(data);
    } catch {
      toast.error("Could not load lost records");
      setData({ companies: [], signals: [], leads: [], opportunities: [], counts: {} });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function restore(type, id, label) {
    if (!window.confirm(`Restore this ${type}? It will re-appear in the ${type} list.`)) return;
    try {
      await api.post(`/trash/restore/${type}/${id}`);
      toast.success(`${TYPE_META[type].label} restored`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  }

  async function purge(type, id) {
    if (!window.confirm(`Permanently delete this ${type}? This CANNOT be undone.`)) return;
    try {
      await api.delete(`/trash/purge/${type}/${id}`);
      toast.success("Permanently deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
  }

  const DATA_KEY = { company: "companies", signal: "signals", lead: "leads", opportunity: "opportunities" };

  async function saveReason(type, id, reason) {
    const { data } = await api.patch(`/trash/${type}/${id}/reason`, { deleted_reason: reason });
    setData(current => {
      const key = DATA_KEY[type];
      const list = current[key] || [];
      return { ...current, [key]: list.map(x => x.id === id ? { ...x, deleted_reason: data.deleted_reason } : x) };
    });
    return data;
  }

  const all = [
    ...(data.companies || []).map(x => ({ ...x, type: "company" })),
    ...(data.signals || []).map(x => ({ ...x, type: "signal" })),
    ...(data.leads || []).map(x => ({ ...x, type: "lead" })),
    ...(data.opportunities || []).map(x => ({ ...x, type: "opportunity" })),
  ].sort((a, b) => new Date(b.deleted_at || 0) - new Date(a.deleted_at || 0));
  const typeFiltered = filter === "all" ? all : all.filter(x => x.type === filter);
  const q = query.trim().toLowerCase();
  const rows = q
    ? typeFiltered.filter(x => (x.title || "").toLowerCase().includes(q) || (x.company?.name || "").toLowerCase().includes(q))
    : typeFiltered;
  const counts = data.counts || {};
  const total = (counts.companies || 0) + (counts.signals || 0) + (counts.leads || 0) + (counts.opportunities || 0);
  const activeFilterCount = filter !== "all" ? 1 : 0;

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="lost-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Lost</h1>
        <p className="text-[13px] text-white/75 mt-1">Companies and pipeline records that were dropped stay here until restored or permanently removed.</p>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search lost records by name or company…"
          entityLabel="lost records"
          count={rows.length}
          variant="universe"
          sparPrompts={[{
            icon: TrendingDown,
            label: "Run loss analysis",
            seed: `Look at everything currently in Lost (${total || 0} records — companies, signals, leads, and opportunities we dropped). Find the common patterns: recurring reasons lost, competitors that keep beating us, which stage we lose deals at most, and any accounts worth revisiting. Give me a prioritized list of 3 fixes to reduce losses going forward.`,
          }]}
          right={(
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-right shrink-0 pr-1">
                <div className="text-[9px] uppercase tracking-widest text-[#8AA0AC]">Total lost</div>
                <div className="font-display text-xl tabular-nums text-[#07539A]" data-testid="lost-total">{total || 0}</div>
              </div>
              <div className="relative">
                <button type="button" onClick={() => setFilterOpen(open => !open)} data-testid="lost-filter-btn"
                        className={`h-9 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-2 shadow-[0_4px_12px_rgba(7,83,154,0.06)] transition-colors ${filterOpen || activeFilterCount ? "border-[#07539A] bg-[#E3F2F8] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#243B53] hover:border-[#07539A]"}`}>
                  <Filter size={14} /> Filter by
                  {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
                </button>
                {filterOpen && (
                  <div className="absolute right-0 top-11 z-30 w-[min(88vw,22rem)] rounded-2xl border border-[#C9DCE6] bg-white shadow-[0_20px_50px_rgba(7,83,154,0.20)] p-4" data-testid="lost-filter-panel">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="font-display text-lg text-[#07539A]">Filter lost records</div>
                        <div className="text-[10px] text-[#78909C] mt-0.5">Narrow down by record type.</div>
                      </div>
                      <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" className="w-8 h-8 rounded-full hover:bg-[#EDF7FB] text-[#607D8B] flex items-center justify-center"><X size={14} /></button>
                    </div>
                    <div className="[&_select]:w-full [&_select]:h-10 [&_select]:rounded-xl [&_select]:border [&_select]:border-[#B8D3DF] [&_select]:bg-[#F8FCFE] [&_select]:px-3 [&_select]:text-[12px] [&_select]:outline-none [&_select:focus]:border-[#00A2D4]">
                      <FilterSelect label="Record type" value={filter} onChange={setFilter} options={TYPE_TABS.map(t => ({
                        key: t.key, label: `${t.label} · ${t.countKey ? counts[t.countKey] || 0 : total || 0}`,
                      }))} />
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#E1EBF0] flex items-center justify-between">
                      <button type="button" onClick={() => setFilter("all")} className="text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear all</button>
                      <button type="button" onClick={() => setFilterOpen(false)} className="h-9 px-4 rounded-xl bg-[#07539A] !text-white text-[12px] font-medium">Show {rows.length.toLocaleString()} records</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        />

        <div className="border border-[#C9DCE6] rounded-[22px] overflow-x-auto bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="lost-table-scroll">
          <div className="grid min-w-[980px] grid-cols-[minmax(200px,2.2fr)_112px_130px_104px_minmax(180px,2fr)_96px_40px_18px] gap-3 items-center px-4 py-3 bg-[#E7F5FB] border-b border-[#C9DCE6] text-[11px] font-semibold text-[#07539A]">
            <div>Company</div>
            <div>Date lost</div>
            <div>Deleted by</div>
            <div>Last stage</div>
            <div>Reason lost</div>
            <div />
            <div />
            <div />
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-[#607D8B]"><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-[#607D8B]">
              <Frown size={20} className="mx-auto mb-2 opacity-40" />
              Nothing here — nothing has been lost.
            </div>
          ) : (
            <div className="min-w-[980px] divide-y divide-[#E1EBF0]">
              {rows.map(row => {
                const meta = TYPE_META[row.type];
                const Icon = meta.icon;
                const isExp = expanded === `${row.type}-${row.id}`;
                const companyName = row.type === "company" ? row.title : (row.company?.name || "—");
                const canEditRow = canRestoreTrashType(user, row.type);
                return (
                  <div key={`${row.type}-${row.id}`} data-testid={`lost-row-${row.id}`}>
                    <div
                      onClick={() => setExpanded(isExp ? null : `${row.type}-${row.id}`)}
                      data-testid={`lost-expand-${row.id}`}
                      className="w-full text-left grid grid-cols-[minmax(200px,2.2fr)_112px_130px_104px_minmax(180px,2fr)_96px_40px_18px] gap-3 items-center px-4 py-3.5 hover:bg-[#F8FCFE] transition-colors cursor-pointer"
                    >
                      <div className="min-w-0 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${meta.color}15`, color: meta.color }}>
                          <Icon size={13} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[12px] font-medium text-[#263F4D] truncate">{companyName}</div>
                        </div>
                      </div>
                      <div className="text-[12px] text-[#526D7A]">{formatDate(row.deleted_at)}</div>
                      <div className="text-[12px] text-[#526D7A] truncate">{row.deleted_by || "—"}</div>
                      <div>
                        <span className="text-[9px] uppercase tracking-widest font-mono px-2 py-1 rounded-full" style={{ background: `${meta.color}15`, color: meta.color }}>{meta.label}</span>
                      </div>
                      <div className="text-[12px] text-[#607D8B] truncate italic">{row.deleted_reason ? row.deleted_reason.replaceAll("_", " ") : "—"}</div>
                      <div onClick={e => e.stopPropagation()}>
                        {canEditRow && (
                        <button onClick={() => restore(row.type, row.id, row.title)}
                                data-testid={`restore-${row.type}-${row.id}`}
                                className="h-8 px-2.5 rounded-lg border border-[var(--success)]/30 text-[var(--success)] hover:bg-[var(--success)]/5 text-[11px] flex items-center gap-1.5 font-medium">
                          <RotateCcw size={11} /> Restore
                        </button>
                        )}
                      </div>
                      <div onClick={e => e.stopPropagation()}>
                        {canPurge && (
                          <button onClick={() => purge(row.type, row.id)}
                                  data-testid={`purge-${row.type}-${row.id}`}
                                  title="Permanently delete"
                                  className="h-8 w-8 rounded-lg border border-[var(--danger)]/30 text-[var(--danger)] hover:bg-[var(--danger)]/5 flex items-center justify-center">
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center justify-center text-[var(--text-dim)]">
                        {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                    </div>
                    {isExp && (
                      <div className="mx-2 mb-3 px-3 md:px-4 py-4 bg-[#EAF7FC] border border-[#BBD8E7] rounded-[22px] space-y-3 shadow-[0_10px_24px_rgba(7,83,154,0.07)]" data-testid={`lost-detail-${row.id}`}>
                        <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#526D7A]">
                          <span className="uppercase tracking-widest font-mono text-[9px]" style={{ color: meta.color }}>{meta.label}</span>
                          <span className="text-[#8AA0AC]">·</span>
                          <span>Lost on {formatDate(row.deleted_at)} by {row.deleted_by || "—"}</span>
                        </div>
                        <EditableField
                          label="Reason lost"
                          value={row.deleted_reason || ""}
                          placeholder="Why was this lost? e.g. Lost to competitor X on price…"
                          testId={`reason-${row.type}-${row.id}`}
                          multiline
                          readOnly={!canEditRow}
                          className="rounded-[18px] border-[#C9DCE6] bg-white shadow-[0_6px_18px_rgba(7,83,154,0.05)]"
                          onSave={async (val) => {
                            await saveReason(row.type, row.id, val.trim());
                            toast.success("Reason updated");
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-[11px] text-[#8AA0AC] flex items-start gap-2">
          <AlertTriangle size={12} className="text-[var(--warning)] shrink-0 mt-0.5" />
          <span>
            Restoring re-links the parent stage where possible. If the parent has already been re-promoted, the restored item will remain unlinked but visible in its list.
            {canPurge && " Purge permanently removes the record — admins only."}
          </span>
        </div>
      </div>
    </div>
  );
}
